import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../content/SiteContentContext.jsx";
import { resolveImage } from "../utils/resolveImage";
import { safeUrl } from "../utils/safeUrl";

const DEFAULT_DESKTOP_IMAGE = "main/bg.webp";

function normalizeClassValue(value, fallback) {
  return String(value || fallback)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function normalizeFit(value, fallback = "cover") {
  return value === "contain" || value === "Contener" ? "contain" : fallback;
}

function isInternalLink(link) {
  return typeof link === "string" && link.startsWith("/");
}

function HeroAction({ text, link, variant = "primary" }) {
  if (!text || !link) return null;
  const className = `hero__button hero__button--${variant}`;
  if (isInternalLink(link)) {
    return (
      <Link to={link} className={className}>
        {text}
      </Link>
    );
  }
  return (
    <a className={className} href={safeUrl(link)}>
      {text}
    </a>
  );
}

function normalizeHeroSlides(hero) {
  const slides = Array.isArray(hero?.slides) ? hero.slides : [];
  const activeSlides = slides.filter((slide) => slide && slide.enabled !== false && slide.enabled !== "No");

  if (activeSlides.length > 0) {
    return activeSlides.map((slide, index) => ({
      id: slide.id || `hero-slide-${index}`,
      eyebrow: slide.eyebrow || slide.type || "",
      title: slide.title || "",
      subtitle: slide.subtitle || "",
      badge: slide.badge || "",
      dateText: slide.dateText || "",
      primaryText: slide.primaryText || slide.ctaText || "",
      primaryLink: slide.primaryLink || slide.ctaLink || "/contacto",
      secondaryText: slide.secondaryText || "",
      secondaryLink: slide.secondaryLink || "",
      imageDesktop: slide.imageDesktop || slide.image || DEFAULT_DESKTOP_IMAGE,
      imageMobile: slide.imageMobile || slide.imageDesktop || slide.image || DEFAULT_DESKTOP_IMAGE,
      imageAlt: slide.imageAlt || slide.title || "Portada INCAP",
      imageFitDesktop: normalizeFit(slide.imageFitDesktop, "cover"),
      imageFitMobile: normalizeFit(slide.imageFitMobile, normalizeFit(slide.imageFitDesktop, "cover")),
      imagePositionDesktop: slide.imagePositionDesktop || "center center",
      imagePositionMobile: slide.imagePositionMobile || "center center",
      overlay: slide.overlay || hero?.defaultOverlay || "Oscuro",
      overlayMobile: slide.overlayMobile || slide.overlay || hero?.defaultOverlay || "Oscuro",
      contentX: slide.contentX || slide.textPosition || "Izquierda",
      contentY: slide.contentY || "Centro",
      textAlign: slide.textAlign || slide.textPosition || "Izquierda",
      contentXMobile: slide.contentXMobile || slide.contentX || slide.textPosition || "Izquierda",
      contentYMobile: slide.contentYMobile || slide.contentY || "Abajo",
      textAlignMobile: slide.textAlignMobile || slide.textAlign || slide.textPosition || "Izquierda",
      showContent: slide.showContent !== false && slide.showContent !== "No",
      showContentMobile: slide.showContentMobile !== false && slide.showContentMobile !== "No",
      minHeightDesktop: slide.minHeightDesktop || "",
      minHeightMobile: slide.minHeightMobile || "",
    }));
  }

  return [
    {
      id: "hero-legacy",
      eyebrow: hero?.welcome || "Bienvenido a",
      title: hero?.title || "INSTITUTO DE CAPACITACIÓN Y APRENDIZAJE PERMANENTE",
      subtitle: hero?.subtitle || "Te acompañamos para que logres más.",
      badge: "",
      dateText: "",
      primaryText: hero?.ctaText || "CONTÁCTANOS",
      primaryLink: hero?.ctaLink || "/contacto",
      secondaryText: hero?.aulaText || "Ingresa al Aula virtual",
      secondaryLink: hero?.aulaLink || "https://aula-virtual.incap.edu.mx/auth/sign-in",
      imageDesktop: DEFAULT_DESKTOP_IMAGE,
      imageMobile: DEFAULT_DESKTOP_IMAGE,
      imageAlt: "Portada INCAP",
      imageFitDesktop: "cover",
      imageFitMobile: "cover",
      imagePositionDesktop: "center center",
      imagePositionMobile: "center center",
      overlay: "Oscuro",
      overlayMobile: "Oscuro",
      textPosition: "Izquierda",
      contentX: "Izquierda",
      contentY: "Centro",
      textAlign: "Izquierda",
      contentXMobile: "Izquierda",
      contentYMobile: "Abajo",
      textAlignMobile: "Izquierda",
      showContent: true,
      showContentMobile: true,
      minHeightDesktop: "",
      minHeightMobile: "",
    },
  ];
}

const Hero = () => {
  const { hero } = useSiteContent();
  const slides = useMemo(() => {
    const normalized = normalizeHeroSlides(hero);
    return hero?.mode === "single" ? normalized.slice(0, 1) : normalized;
  }, [hero]);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex] || slides[0];
  const autoplay = hero?.autoplay !== false && hero?.autoplay !== "No";
  const intervalMs = Number(hero?.intervalMs) > 0 ? Number(hero.intervalMs) : 6500;
  const showControls = slides.length > 1;
  const hasContent =
    activeSlide?.showContent &&
    (activeSlide.eyebrow ||
      activeSlide.badge ||
      activeSlide.title ||
      activeSlide.subtitle ||
      activeSlide.dateText ||
      activeSlide.primaryText ||
      activeSlide.secondaryText);
  const sectionClass = [
    "hero",
    `hero--${normalizeClassValue(activeSlide?.overlay, "oscuro")}`,
    `hero--x-${normalizeClassValue(activeSlide?.contentX, "izquierda")}`,
    `hero--y-${normalizeClassValue(activeSlide?.contentY, "centro")}`,
    `hero--align-${normalizeClassValue(activeSlide?.textAlign, "izquierda")}`,
    `hero--mobile-overlay-${normalizeClassValue(activeSlide?.overlayMobile, "oscuro")}`,
    `hero--mobile-x-${normalizeClassValue(activeSlide?.contentXMobile, "izquierda")}`,
    `hero--mobile-y-${normalizeClassValue(activeSlide?.contentYMobile, "abajo")}`,
    `hero--mobile-align-${normalizeClassValue(activeSlide?.textAlignMobile, "izquierda")}`,
    !hasContent ? "hero--media-only" : "",
    activeSlide?.showContentMobile === false ? "hero--mobile-media-only" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    setActiveIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (!autoplay || slides.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [autoplay, intervalMs, slides.length]);

  const goTo = (index) => {
    setActiveIndex((index + slides.length) % slides.length);
  };

  return (
    <section
      className={sectionClass}
      style={{
        "--hero-image-fit-desktop": activeSlide.imageFitDesktop,
        "--hero-image-fit-mobile": activeSlide.imageFitMobile,
        "--hero-image-position-desktop": activeSlide.imagePositionDesktop,
        "--hero-image-position-mobile": activeSlide.imagePositionMobile,
        "--hero-min-height-desktop": activeSlide.minHeightDesktop || undefined,
        "--hero-min-height-mobile": activeSlide.minHeightMobile || undefined,
      }}
    >
      <picture className="hero__media" key={activeSlide.id}>
        <source media="(max-width: 767px)" srcSet={resolveImage(activeSlide.imageMobile)} />
        <img src={resolveImage(activeSlide.imageDesktop)} alt={activeSlide.imageAlt} />
      </picture>

      <div className="hero__overlay" />

      {hasContent ? (
        <div className="hero__content">
          <div className="hero__copy">
            {activeSlide.eyebrow ? <p className="hero__eyebrow">{activeSlide.eyebrow}</p> : null}
            {activeSlide.badge ? <span className="hero__badge">{activeSlide.badge}</span> : null}
            {activeSlide.title ? <h1>{activeSlide.title}</h1> : null}
            {activeSlide.subtitle ? <p className="hero__subtitle">{activeSlide.subtitle}</p> : null}
            {activeSlide.dateText ? <p className="hero__date">{activeSlide.dateText}</p> : null}
            <div className="hero__actions">
              <HeroAction text={activeSlide.primaryText} link={activeSlide.primaryLink} variant="primary" />
              <HeroAction text={activeSlide.secondaryText} link={activeSlide.secondaryLink} variant="secondary" />
            </div>
          </div>
        </div>
      ) : null}

      {showControls ? (
        <>
          <button className="hero__arrow hero__arrow--prev" type="button" aria-label="Portada anterior" onClick={() => goTo(activeIndex - 1)}>
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button className="hero__arrow hero__arrow--next" type="button" aria-label="Portada siguiente" onClick={() => goTo(activeIndex + 1)}>
            <i className="fa-solid fa-chevron-right" />
          </button>
          <div className="hero__dots" aria-label="Portadas destacadas">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                className={index === activeIndex ? "hero__dot hero__dot--active" : "hero__dot"}
                type="button"
                aria-label={`Ver portada ${index + 1}`}
                aria-current={index === activeIndex}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default Hero;
